import React, { FC, useState, useEffect, useMemo } from 'react';
import { DateInputProps } from './DateInput.types';
import { Input } from '../Input';

function formatValueToDate(value: string) {
  const chunks = value.split('-');
  return chunks[1] + '/' + chunks[2] + '/' + chunks[0];
}

function formatValueToDateSafari(value: string) {
  return value.replace(/-/g, '/');
}

function formatDateToValue(date: string) {
  const chunks = date.split('/');
  return chunks[2] + '-' + chunks[0] + '-' + chunks[1];
}

function hasNativePickerSupport() {
  const input = document.createElement('input');
  input.setAttribute('type', 'date');
  input.value = '2018-01-01';
  return !!input.valueAsDate;
}

/** Clone an event object with a custom 'value' */
function cloneEvent(evt: React.SyntheticEvent<HTMLInputElement>, value: any) {
  return {
    ...evt,
    target: {
      ...evt.target,
      value: value,
    },
  };
}

export const DateInput: FC<DateInputProps> = props => {
  const hasNativePicker = useMemo(() => hasNativePickerSupport(), []);
  const { ...rest } = props;
  const [value, setValue] = useState('');

  const formatDateToValueStrategy = hasNativePicker
    ? formatDateToValue
    : formatValueToDateSafari;
  const formatValueToDateStrategy = hasNativePicker
    ? formatValueToDate
    : formatValueToDateSafari;

  useEffect(() => {
    if (props?.value) setValue(formatDateToValueStrategy(props.value));
  }, [formatDateToValueStrategy, props.value]);

  const currentYear = new Date().getUTCFullYear();

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.trim().length === 0) return;

    setValue(value);

    const fauxEvent = cloneEvent(e, formatValueToDateStrategy(value));

    if (props.onBlur)
      props.onBlur(fauxEvent as React.FocusEvent<HTMLInputElement>);
    if (props.onChange)
      props.onChange(fauxEvent as React.ChangeEvent<HTMLInputElement>);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let year = '0000';
    const dateArr = value.split('-');
    if (dateArr.length) year = dateArr[0];

    if (Number(year) > currentYear) return;

    setValue(value);

    if (props.onChange) {
      const fauxEvent = cloneEvent(e, formatValueToDateStrategy(value));
      props.onChange(fauxEvent as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <Input
      {...rest}
      active
      value={value}
      type="date"
      placeholder="mm/dd/yyyy"
      onBlur={onBlur}
      onChange={onChange}
    />
  );
};

DateInput.defaultProps = {
  type: 'text',
};
